import type { IconName } from '@w7/ui'
import type { Role } from '@/data/types'
import { canAccessLogPage, type LogPageId } from '@/features/logs/access'

/**
 * The navigation model.
 *
 * Here the rail is grouped by the question being asked:
 *   Run the book   — what is happening right now
 *   People         — who is beneath me
 *   Money          — settled P/L
 *   Logs           — Winner All Logs pages (TM / Master only)
 * Settings and sign-out leave the rail entirely and live under the account menu.
 */

export interface NavItem {
  id: string
  label: string
  to: string
  icon: IconName
  /** Matches child routes too, e.g. /exposure/market/xyz. */
  match?: string
  /** Extra prefixes that keep this rail item current. */
  aliases?: string[]
  description: string
  /** Rendered as a disclosure beneath the item — the legacy menu dropdowns. */
  children?: { id: string; label: string; to: string }[]
  /**
   * When set, the item is shown only for these roles. Omitted = every signed-in
   * console role. Logs pages pin Winner All Logs access here and in LogAccessGate.
   */
  roles?: readonly Role[]
  /** Links a Logs nav row to the shared access table. */
  logPageId?: LogPageId
}

export interface NavGroup {
  id: string
  label: string
  items: NavItem[]
}

const RUN_ITEMS: NavItem[] = [
  {
    id: 'overview',
    label: 'Overview',
    to: '/overview',
    icon: 'overview',
    match: '/overview',
    description: 'Position, trend and anything needing attention',
  },
  {
    id: 'net-exposure',
    label: 'Net Exposure',
    to: '/book-management/net-exposure',
    icon: 'net-exposure',
    match: '/book-management/net-exposure',
    aliases: ['/net-exposure'],
    description:
      'Open books for this Aggregate group only (Lab Aggregate Net Exposure)',
  },
]

const STATIC_GROUPS: NavGroup[] = [
  {
    id: 'people',
    label: 'People',
    items: [
      {
        id: 'hierarchy-search',
        label: 'Hierarchy Search',
        to: '/hierarchy-search',
        icon: 'search',
        match: '/hierarchy-search',
        description:
          'Find anyone in your Aggregate group and see Player → Master → SM → DM → TM',
      },
      {
        id: 'aggregate-accounts',
        label: 'Aggregate Accounts',
        to: '/aggregate/accounts',
        icon: 'users',
        match: '/aggregate/accounts',
        description: 'Linked Aggregate group members — credit limit, Take, Give, and status',
      },
    ],
  },
  {
    id: 'money',
    label: 'Money',
    items: [
      {
        id: 'my-pnl',
        label: 'My P/L',
        to: '/reports/my-pnl',
        icon: 'pnl',
        description: 'Settled book P/L by Sub Game — Lab master-dashboard (old)',
      },
      {
        id: 'my-pnl-new',
        label: 'My P/L New',
        to: '/reports/my-pnl-new',
        icon: 'pnl',
        description: 'Settled book P/L — Lab master-dashboard-new (7-day)',
      },
    ],
  },
  {
    id: 'logs',
    label: 'Logs',
    items: [
      {
        id: 'logs-cl-transfers',
        label: 'CL Transfers',
        to: '/logs/cl-transfers',
        icon: 'sliders',
        logPageId: 'cl-transfers',
        roles: ['triple-master', 'master'],
        description: 'Credit-limit changes across the hierarchy',
      },
      {
        id: 'logs-multiple-device-ids',
        label: 'Multiple Device Ids',
        to: '/logs/multiple-device-ids',
        icon: 'users',
        logPageId: 'multiple-device-ids',
        roles: ['triple-master'],
        description: 'Players sharing the same device id',
      },
      {
        id: 'logs-betfair-rates',
        label: 'BetfairRates Logs',
        to: '/logs/betfair-rates',
        icon: 'sport',
        logPageId: 'betfair-rates',
        roles: ['master'],
        description: 'Betfair rate and min-bet changes',
      },
      {
        id: 'logs-invalid-bets',
        label: 'Invalid Bets',
        to: '/logs/invalid-bets',
        icon: 'warning',
        logPageId: 'invalid-bets',
        roles: ['master'],
        description: 'VAR and invalid bet logs',
      },
      {
        id: 'logs-blocked-device-ids',
        label: 'Blocked Device Ids',
        to: '/logs/blocked-device-ids',
        icon: 'lock',
        logPageId: 'blocked-device-ids',
        roles: ['master'],
        description: 'Blocked device ids and linked players',
      },
      {
        id: 'logs-blocked-ip',
        label: 'Blocked IP',
        to: '/logs/blocked-ip',
        icon: 'lock',
        logPageId: 'blocked-ip',
        roles: ['master'],
        description: 'Blocked IPs, comments, and players',
      },
      {
        id: 'logs-login-from-blocked-ip',
        label: 'Login From Blocked IP',
        to: '/logs/login-from-blocked-ip',
        icon: 'shield',
        logPageId: 'login-from-blocked-ip',
        roles: ['master'],
        description: 'Sign-ins that came from a blocked IP',
      },
    ],
  },
]

/** The rail for one operator. */
export function buildNav(): NavGroup[] {
  return [
    { id: 'run', label: 'Run the book', items: [...RUN_ITEMS] },
    ...STATIC_GROUPS,
  ]
}

/**
 * Full rail, used by the command palette so a page an operator can open by URL
 * is also findable by name.
 */
export const NAV: NavGroup[] = buildNav()

export const ALL_NAV_ITEMS: NavItem[] = NAV.flatMap((group) => group.items)

function itemVisibleToRole(item: NavItem, role: Role | undefined): boolean {
  if (item.logPageId) return canAccessLogPage(role, item.logPageId)
  if (!item.roles) return true
  return Boolean(role && item.roles.includes(role))
}

/** Role filter over a built rail — drops empty groups (Logs for Super Master). */
export function filterNavForRole(groups: NavGroup[], role: Role | undefined): NavGroup[] {
  return groups
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => itemVisibleToRole(item, role)),
    }))
    .filter((group) => group.items.length > 0)
}

/** Rail + Jump-to catalogue for the signed-in role. Empty groups are dropped. */
export function navForRole(role: Role | undefined): NavGroup[] {
  return filterNavForRole(NAV, role)
}

export function navItemsForRole(role: Role | undefined): NavItem[] {
  return navForRole(role).flatMap((group) => group.items)
}

export function isNavItemActive(pathname: string, item: NavItem): boolean {
  if (item.to === '/' && !item.match) return pathname === '/'
  const prefixes = [item.match ?? item.to, ...(item.aliases ?? [])]
  return prefixes.some((prefix) => {
    if (prefix === '/') return pathname === '/'
    return pathname === prefix || pathname.startsWith(`${prefix}/`)
  })
}
